import React, { useState, ChangeEvent, useEffect, useRef } from 'react';

import { ActionButton } from '@notino/react-styleguide-v20';

import { useValidationContext } from '@context/ValidationContext';
import { ReactFCV } from '@types';
import { EScrollRegistryKeys, registerScrollCallback } from '@utils/scrollRegistry';

import { IInputGroupWithButtonProps } from './model';
import { StyledInput, StyledInputGroup, StyledInputGroupWithButton, StyledLabel } from './styled';

export const InputGroupWithButton: ReactFCV<IInputGroupWithButtonProps> = ({
  label,
  inputGroupProps,
  actionButtonProps,
  ...rest
}) => {
  const { reportError } = useValidationContext();

  const [internalInputValue, setInternalInputValue] = useState<string>(inputGroupProps.value ?? '');
  const internalOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInternalInputValue(event.target.value);
  };

  const internalOnClick = () => {
    actionButtonProps.onClick(internalInputValue);
    inputGroupProps.clearAfterSubmit && setInternalInputValue('');
    inputGroupProps.resetVoucherCode && inputGroupProps.resetVoucherCode();
  };

  useEffect(() => {
    setInternalInputValue(inputGroupProps.value ?? '');
  }, [inputGroupProps.value]);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const input = ref.current?.querySelector('input');
    if (ref.current) {
      registerScrollCallback(EScrollRegistryKeys.INPUT_GROUP, () => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (input) {
          input.focus();
        }
      });
    }

    if (inputGroupProps.error) {
      reportError('inputWithButton', inputGroupProps.error);
    }
  }, [inputGroupProps.error, reportError]);

  return (
    <div {...rest}>
      <StyledLabel data-testid="label">{label}</StyledLabel>
      <StyledInputGroupWithButton ref={ref}>
        <StyledInputGroup {...inputGroupProps}>
          <StyledInput
            type="text"
            placeholder={inputGroupProps.placeHolder}
            value={internalInputValue}
            onChange={internalOnChange}
            className={inputGroupProps['data-testid']}
            errorBorder={inputGroupProps.errorBorder}
          />
        </StyledInputGroup>

        <ActionButton
          data-testid="btn"
          {...actionButtonProps}
          className={actionButtonProps['data-testid']}
          onClick={internalOnClick}>
          {actionButtonProps.buttonText}
        </ActionButton>
      </StyledInputGroupWithButton>
    </div>
  );
};
